// Galaxy choice and join (04 §4.3). Joining needs a confirmed email.
import type { GalaxyListItem } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { endpoints } from '../api/endpoints';
import { queryKeys, useGalaxies } from '../api/queries';
import { AuthLayout } from '../components/AuthLayout';
import { Button, ErrorNotice, Notice, Spinner, TextField } from '../components/ui';
import { formatNumber } from '../labels';
import { useSession } from '../state/session';
import { useLogout } from '../state/useLogout';

/** The home system (first owned system) or the map when the player has none (G-49). */
export const homePath = (galaxyId: string, homeSystemId: string | undefined) =>
  homeSystemId ? `/g/${galaxyId}/systems/${homeSystemId}` : `/g/${galaxyId}`;

function GalaxyCard({ galaxy, canJoin }: { galaxy: GalaxyListItem; canJoin: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [joining, setJoining] = useState(false);
  const [name, setName] = useState('');
  const join = useMutation({
    mutationFn: () => endpoints.joinGalaxy(galaxy.id, name),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.galaxies });
      navigate(homePath(galaxy.id, data.homeSystemId));
    },
  });

  return (
    <li className="hud-cut-sm border border-line bg-void/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-text">{galaxy.name}</h2>
          <p className="mt-1 text-xs text-muted">
            {formatNumber(galaxy.playerCount)} commanders
            {galaxy.speedPct !== 100 ? ` · test speed ×${galaxy.speedPct / 100}` : ''}
          </p>
        </div>
        {galaxy.joined ? (
          <Button
            onClick={async () => {
              try {
                const me = await queryClient.fetchQuery({
                  queryKey: queryKeys.galaxyMe(galaxy.id),
                  queryFn: () => endpoints.galaxyMe(galaxy.id),
                });
                navigate(homePath(galaxy.id, me.systems[0]?.id));
              } catch {
                navigate(`/g/${galaxy.id}`);
              }
            }}
          >
            Enter galaxy
          </Button>
        ) : !joining ? (
          <Button disabled={!canJoin} onClick={() => setJoining(true)}>
            Join galaxy
          </Button>
        ) : null}
      </div>
      {joining && !galaxy.joined ? (
        <form
          className="mt-4 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            join.mutate();
          }}
        >
          <TextField
            label="Commander name"
            required
            minLength={3}
            maxLength={20}
            autoComplete="nickname"
            hint="3–20 letters, digits, spaces, . _ -"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {join.error ? <ErrorNotice error={join.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={join.isPending}>
              Start
            </Button>
            <Button variant="secondary" onClick={() => setJoining(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

export function GalaxiesScreen() {
  const account = useSession((s) => s.account);
  const galaxies = useGalaxies();
  const { logout } = useLogout();
  const resend = useMutation({ mutationFn: endpoints.resendVerification });
  const verified = account?.emailVerified ?? false;

  return (
    <AuthLayout title="Choose a galaxy">
      {!verified ? (
        <Notice>
          We sent a confirmation link to{' '}
          <span className="font-medium text-text">{account?.email}</span>. Open it to join a galaxy.{' '}
          <button
            type="button"
            className="font-semibold text-info underline disabled:opacity-50"
            disabled={resend.isPending || resend.isSuccess}
            onClick={() => resend.mutate()}
          >
            {resend.isSuccess ? 'Sent' : 'Send again'}
          </button>
          <span className="mt-1 block text-xs text-muted">
            Wrong address?{' '}
            <button type="button" className="text-info underline" onClick={logout}>
              Log out
            </button>{' '}
            and register again.
          </span>
        </Notice>
      ) : null}
      {galaxies.isPending ? <Spinner /> : null}
      {galaxies.error ? <ErrorNotice error={galaxies.error} /> : null}
      <ul className="space-y-3">
        {galaxies.data?.items.map((galaxy) => (
          <GalaxyCard key={galaxy.id} galaxy={galaxy} canJoin={verified} />
        ))}
      </ul>
      {galaxies.data?.items.length === 0 ? <Notice>No galaxy is open yet.</Notice> : null}
    </AuthLayout>
  );
}
